import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormBuilder } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GenerateIntakeEvictionForm, collectInvalidControls, invalidGroupNames, ToStepCompletionPayload } from '@ee/common/forms';
import { AnalyticsService } from '@ee/common/analytics';
import { CaseSettings, Eviction, IntakeEviction, Organization, PropertyOwner, User, Workflow, mapDocumentGenerationToDocuments } from '@ee/common/models';
import { IntakeEvictionService } from '@ee/common/services';
import { AccountType, IntakeEvictionType, SubscriptionPlan, IntakeFormSections } from '@ee/common/enums';
import { MatStepper } from '@angular/material/stepper';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { EVICTION_REASONS_CLAIMS_MAP } from '@ee/common/constants';
import { IntakeEvictionFormService } from '../services/intake-eviction-form.service';

@Component({
  selector: 'ee-create-eviction',
  templateUrl: './create-eviction.component.html',
  styleUrls: ['./create-eviction.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  standalone: false
})
export class CreateEvictionComponent implements OnInit, OnChanges, OnDestroy {
  subs: Subscription[] = [];
  attorneyAccount = AccountType.ATTORNEY;
  saving = false;
  loading = false;
  currentStep = 0;
  public selectedWorkflow: Workflow | undefined = undefined;

  /** Map from document field name to upload_id, populated by ee-eviction-documentation. */
  private documentUploadIds: Record<string, string> = {};
  /** True when all required document uploads have completed scanning. */
  private documentUploadsReady = false;

  @Input() attorneyId: string;

  @Input() forceIntakeForm = false;

  /**
   * Property-linked PM submission mode (additive; default off so the public intake app and
   * attorney/client portals are unaffected): skips the Contact/Business Information step —
   * the server synthesizes `client_details`/`user_details` from the case's linked property and
   * the submitting PM user, and the attorney resolves the actual client at inbox-process time.
   * Only meaningful together with `forceIntakeForm`.
   */
  @Input() skipClientStep = false;

  /**
   * The linked property's owners (property-linked PM mode): their legal names REPLACE the
   * previously-used-plaintiffs autocomplete on the Plaintiff Name field, with the primary owner
   * (or sole owner) preselected. Unset keeps the built-in plaintiff-history behavior.
   */
  @Input() propertyOwners?: PropertyOwner[];

  /**
   * True when the submitter is authenticated (the PM "send to attorney" slide-in), as opposed to
   * an anonymous public intake visitor. `forceIntakeForm` alone can't distinguish the two — it only
   * means "show the attorney's configured intake fields." Uploads need to know which: an anonymous
   * submitter's files are staged attorney-scoped via the intake upload endpoint (no auth context to
   * key on), while an authenticated submitter's files must go through the authenticated upload
   * endpoint so ownership matches what the eventual submit (submitHandler) resolves it as. Default
   * off keeps the public intake app's existing (anonymous) upload behavior unchanged.
   */
  @Input() authenticatedUpload = false;

  @Input() firstStepLabel = 'Client & Workflow';

  @Input() secondStepLabel = 'Claims';

  @Input() lastStepLabel = 'Documentation';

  @Input() loggedInUser: User | undefined;

  @Input() loggedInOrg: Organization | undefined;

  @Input() currentPlan: SubscriptionPlan | undefined;

  @Input() workflows: Workflow[];

  @Input() selectedClientId?: string;

  @Input() cloneCase?: Eviction;

  @Input() caseSettings: CaseSettings;

  /**
   * Optional override for how the completed intake is submitted. When set, `save()` routes the
   * built `IntakeEviction` through this handler instead of the default public-intake POST — used by
   * the PM "send to attorney" slide-in, which must submit through the handoff endpoint (create
   * intake + link + freeze the case) rather than the anonymous public endpoint. Default (unset)
   * keeps the existing behavior, so the public intake app and attorney portal are unaffected.
   */
  @Input() submitHandler?: (intake: IntakeEviction) => Observable<string | undefined>;

  @Output() addClient: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() evictionCreated: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();

  protected readonly EVICTION_REASONS_CLAIMS_MAP = EVICTION_REASONS_CLAIMS_MAP;

  public intakeFormSections = IntakeFormSections;

  public smallScreen = false;

  constructor(
    private fb: UntypedFormBuilder,
    private intakeEvictionService: IntakeEvictionService,
    private snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private changeDetectorRef: ChangeDetectorRef,
    public intakeEvictionFormService: IntakeEvictionFormService,
    private analyticsService: AnalyticsService
  ) {}

  /**
   * A step gate or the submit button refused to advance. Record WHY, for diagnosis only.
   *
   * Deliberately OBSERVE-ONLY: this changes nothing the user sees and nothing about how the form
   * behaves. The gates are intended behaviour — a claims total above zero is a real requirement,
   * and the form already tells the user so on screen.
   *
   * It exists because when a customer reports "I can't submit your form" there is currently nothing
   * to investigate: no request is made, so the backend logs are empty, and the reporter usually
   * cannot say which field stopped them. Recording the invalid control paths and the validator keys
   * that failed makes those reports answerable from telemetry instead of guesswork.
   *
   * Only paths + validator KEYS are sent. Never values — this form carries tenant names, addresses,
   * SSNs and dollar amounts.
   */
  private reportBlocked(where: string): void {
    try {
      // Always the whole form, never a subtree: `where` already says which gate refused, and both
      // fields below must describe the same tree or the paths and the group names disagree.
      const root = this.intakeEvictionFormService.splitForm;
      const invalid = collectInvalidControls(root);
      const groups = invalidGroupNames(root);
      this.analyticsService.capture('intake_submit_blocked', {
        blocked_at: where,
        invalid_count: invalid.length,
        // Cap the array: a pathological form shouldn't produce an unbounded event property.
        invalid_paths: invalid.slice(0, 25).map((i) => i.path),
        invalid_errors: invalid.slice(0, 25).map((i) => `${i.path}:${i.errors.join('|')}`),
        // An invalid control the user never touched is the strong signal that it is unreachable.
        untouched_invalid_paths: invalid.filter((i) => !i.touched).slice(0, 25).map((i) => i.path),
        invalid_groups: groups,
        eviction_reason: this.intakeEvictionFormService.claimDetails?.get('eviction_reason')?.value ?? null,
        is_public_intake: !!this.forceIntakeForm,
        current_step: this.currentStep
      });
    } catch {
      // Telemetry must never be the reason a user can't proceed.
    }
  }

  async ngOnInit() {
    this.loading = true;
    try {
      await this.initializeWizard();
    } catch (err) {
      // Without this, a rejected initialize() left `loading` true forever: the overlay spinner covered
      // the wizard with no error and no way forward. setupForms() awaits firstValueFrom() on the intake
      // form fetch, so any HTTP failure there rejected all the way out of ngOnInit and stranded the user.
      console.error('Failed to initialize the eviction wizard', err);
      this.snackBar.open(
        'We could not load the intake form. Please refresh and try again.',
        'Dismiss',
        { duration: 10_000 },
      );
    } finally {
      this.loading = false;
    }
  }

  /** Everything ngOnInit used to do inline, so the loading flag can be cleared in a finally. */
  private async initializeWizard() {
    // calculate screen size
    this.subs.push(
      this.breakpointObserver
        .observe([Breakpoints.Small, Breakpoints.HandsetPortrait])
        .subscribe((state: BreakpointState) => {
          this.smallScreen = state.matches;
        })
    );

    const accountType: AccountType | undefined = this.loggedInOrg?.type;
    const showLoggedInForm = this.loggedInUser && this.loggedInOrg && this.currentPlan && !this.forceIntakeForm;
    let attorneyId = this.attorneyId;
    if (!attorneyId) {
      attorneyId = this.attorneyAccount === accountType ? this.loggedInOrg.id : this.loggedInOrg.attorney?.id;
    }
    if (showLoggedInForm) {
      if (accountType === AccountType.ATTORNEY) {
        attorneyId = this.loggedInOrg.id;
      } else {
        attorneyId = this.loggedInOrg.attorney?.id;
        this.firstStepLabel = 'Attorney';
        this.secondStepLabel = 'Claims';
        this.lastStepLabel = 'Documentation';
      }
    }

    // Property-linked PM mode: seed the plaintiff options from the property's owners BEFORE the
    // sections build (setupForms), whether that happens below (skipClientStep) or on step 1's Next.
    if (this.propertyOwners?.length) {
      const names = this.propertyOwners.map((o) => o.legal_name).filter((n) => !!n);
      const primary = this.propertyOwners.find((o) => o.is_primary)?.legal_name ?? names[0];
      this.intakeEvictionFormService.setPropertyPlaintiffs(names, primary);
    }

    await this.intakeEvictionFormService.initialize(
      this.forceIntakeForm,
      attorneyId,
      this.loggedInUser,
      this.loggedInOrg,
      this.cloneCase
    );

    if (this.forceIntakeForm && this.skipClientStep) {
      // The Contact/Business step is skipped, so its Next (which normally runs setupForms) never
      // fires — build the intake sections now so the stepper can start at Tenant & Property.
      await this.intakeEvictionFormService.setupForms(this.cloneCase);
      // Disable the hidden groups so their required validators can't block splitForm validity;
      // save() also omits these sections from the payload (the server synthesizes them).
      this.intakeEvictionFormService.workflowDetails.get('user_details')?.disable();
      this.intakeEvictionFormService.workflowDetails.get('client_details')?.disable();
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['workflows'] && this.workflows?.length) {
      this.intakeEvictionFormService.updateWorkflow(this.workflows);
    }
  }

  async goForward(currentStep: number, stepper: MatStepper) {
    if (currentStep === 1) {
      if (this.loggedInOrg?.type === this.attorneyAccount && this.intakeEvictionFormService.workflowDetails.invalid) {
        this.intakeEvictionFormService.workflowDetails.markAllAsTouched();
        return;
      }
      await this.intakeEvictionFormService.setupForms(this.cloneCase);
    } else if (
      currentStep === 3 &&
      (this.intakeEvictionFormService.propertyDetails.invalid || this.intakeEvictionFormService.tenantDetails.invalid)
    ) {
      this.intakeEvictionFormService.tenantDetails.markAllAsTouched();
      this.intakeEvictionFormService.propertyDetails.markAllAsTouched();
      this.reportBlocked('step3');
      return;
    } else if (
      currentStep === 4 &&
      (this.intakeEvictionFormService.claimDetails.invalid || this.intakeEvictionFormService.leaseTermsDetails.invalid)
    ) {
      this.intakeEvictionFormService.claimDetails.markAllAsTouched();
      this.intakeEvictionFormService.leaseTermsDetails.markAllAsTouched();
      this.reportBlocked('step4');
      return;
    }
    this.currentStep = currentStep;
    this.intakeEvictionFormService.generateEvictionDetails();
    this.changeDetectorRef.detectChanges();
    stepper.next();
  }

  goBack(currentStep: number, stepper: MatStepper) {
    if (currentStep === 2) {
      this.intakeEvictionFormService.removeForms();
    }
    this.currentStep = currentStep - 1;
    stepper.previous();
  }

  stepChanged() {
    // scroll to top
    window.document.querySelector('.mat-horizontal-content-container')?.scrollTo(0, 0);
  }

  updateSelectedWorkflow(workflow: Workflow) {
    this.selectedWorkflow = workflow;
    this.intakeEvictionFormService.updateSelectedWorkflow(workflow);
  }

  onUploadIdsChange(uploadIds: Record<string, string>): void {
    this.documentUploadIds = uploadIds;
  }

  onUploadsReadyChange(ready: boolean): void {
    this.documentUploadsReady = ready;
  }

  save() {
    if (this.intakeEvictionFormService.splitForm.invalid) {
      this.intakeEvictionFormService.documents.markAllAsTouched();
      this.reportBlocked('save');
      return;
    }

    // Gate on upload completion: every required document field must have a
    // completed upload_id (upload + malware scan finished). Form validity alone
    // is not sufficient — a File being selected satisfies Validators.required
    // even while the upload is still in progress.
    if (!this.documentUploadsReady) {
      this.snackBar.open('Please wait for all required documents to finish uploading before submitting.', 'Dismiss', {
        duration: 4000
      });
      return;
    }

    this.saving = true;

    const workflowDetails = this.intakeEvictionFormService.workflowDetails.value;
    const claimDetails = this.intakeEvictionFormService.claimDetails.value;

    // merge split data into intake form
    const form = GenerateIntakeEvictionForm(
      this.fb,
      this.attorneyId ? IntakeEvictionType.INTAKE_FORM : IntakeEvictionType.EXISTING_CLIENT,
      // Property-linked PM mode omits client_details — the server synthesizes it.
      !this.skipClientStep
    );
    const intakeEviction: IntakeEviction = form.value;
    intakeEviction.attorney_id = workflowDetails.attorney_id;
    if ((!this.loggedInOrg || this.loggedInOrg.type !== AccountType.CLIENT) && !this.skipClientStep) {
      intakeEviction.client_details = workflowDetails.client_details;
      intakeEviction.user_details = workflowDetails.user_details;
    }
    intakeEviction.intake_form_id = this.intakeEvictionFormService.intakeForm?.id;
    intakeEviction.selected_workflow_id = workflowDetails.workflow_id;
    intakeEviction.selected_attorney_user_id = workflowDetails.selected_attorney_user_id;
    intakeEviction.note = this.intakeEvictionFormService.splitForm.value.note;
    const rawStepCompletionDetails = this.intakeEvictionFormService.splitForm.value.step_completion_details;
    // This path never calls CompleteWorkflowStepComponent.save() — the component grafts its form on
    // via [parentForm] and we read the raw value here — so the wire conversions it applies would be
    // missing unless we apply them too. ToStepCompletionPayload is that shared step (a MULTI_SELECT
    // array must be joined or Jackson rejects the whole request); keep it, not a local copy.
    //
    // The `documents` mapping stays here because it is specific to the create path: the
    // action-center path lets the server derive it in EvictionService.saveStepCompletion. Without
    // it the selected templates are dropped and no documents generate on create. Built as a new
    // object rather than mutating the form-value snapshot directly.
    const stepCompletionDetails = rawStepCompletionDetails
      ? {
        ...ToStepCompletionPayload(rawStepCompletionDetails),
        documents: mapDocumentGenerationToDocuments(rawStepCompletionDetails.document_generation)
      }
      : rawStepCompletionDetails;
    intakeEviction.step_completion_details = stepCompletionDetails;
    if (this.cloneCase) {
      intakeEviction.original_case_id = this.cloneCase.case_id;
      intakeEviction.existing_documents = this.intakeEvictionFormService.existingDocuments?.value || [];
    }

    intakeEviction.partial_payments = claimDetails.partial_payments; // populate partial payments list
    intakeEviction.eviction_details = this.intakeEvictionFormService.generateEvictionDetails();

    // Attach upload_ids from the upload-on-select flow (new pattern)
    intakeEviction.upload_ids = this.documentUploadIds;

    const submit$ = this.submitHandler
      ? this.submitHandler(intakeEviction)
      : this.intakeEvictionService.saveIntakeEvictionWithUploads(intakeEviction.type, intakeEviction);

    this.subs.push(
      submit$.subscribe({
        next: (caseId: string | undefined) => {
          this.evictionCreated.emit(caseId);
        },
        error: (error: HttpErrorResponse) => {
          // BadRequestAlertException maps its descriptive message to ProblemDetail.title and the
          // short entityName (e.g. "upload", "case") to .detail — title is the one worth showing.
          const message = error.error?.title || 'An error occurred submitting your request. Please try again shortly.';
          this.snackBar.open(message, 'Dismiss', { duration: 5000 });
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.intakeEvictionFormService.clearForm();
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
